import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { CheckCircle2 } from 'lucide-react';
import { apiUrl, getCsrfToken } from '../api';
import './TrainingLog.css';

const TrainingLog = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({
        training_date: new Date().toISOString().split('T')[0],
        training_type: 'Annual',
        trainer_name: '',
        topic_description: 'Annual SB 553 Workplace Violence Prevention Training',
        attendee_count: '',
        documentation_url: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setErrorMessage('');
        setLoading(true);
        try {
            const csrfToken = await getCsrfToken();
            const response = await fetch(apiUrl('/api/training'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify({
                    ...formData,
                    attendee_count: parseInt(formData.attendee_count) || 0
                })
            });

            let result;
            try {
                result = await response.json();
            } catch (error) {
                throw new Error('Invalid training response');
            }
            if (!response.ok || result.success !== true) {
                throw new Error('Training request was not accepted');
            }

            setSuccess(true);
            setTimeout(() => navigate('/dashboard'), 2000);
        } catch (error) {
            setErrorMessage('We could not save this training record. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="training-log-container">
                <div className="success-message" role="status">
                    <div className="success-icon">
                        <CheckCircle2 size={48} aria-hidden="true" />
                    </div>
                    <h2>Training Record Saved</h2>
                    <p>Redirecting to dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="training-log-container">
            <div className="form-card">
                <div className="form-header">
                    <Logo size="medium" />
                    <h1>Workplace Violence Training Record</h1>
                    <p>Log a completed training session for your internal records.</p>
                </div>

                {errorMessage && <div className="error-message" role="alert">{errorMessage}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="training-date">Training Date</label>
                            <input
                                id="training-date"
                                type="date"
                                name="training_date"
                                value={formData.training_date}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="training-type">Training Type</label>
                            <select
                                id="training-type"
                                name="training_type"
                                value={formData.training_type}
                                onChange={handleChange}
                                required
                            >
                                <option value="Initial">Initial (New Hires)</option>
                                <option value="Annual">Annual Refresher</option>
                                <option value="Post-Incident">Post-Incident Training</option>
                                <option value="New Hazard">New Hazard Identified</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="trainer-name">Trainer Name</label>
                            <input
                                id="trainer-name"
                                type="text"
                                name="trainer_name"
                                value={formData.trainer_name}
                                onChange={handleChange}
                                placeholder="Name of person conducting training"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="attendee-count">Attendee Count</label>
                            <input
                                id="attendee-count"
                                type="number"
                                name="attendee_count"
                                value={formData.attendee_count}
                                onChange={handleChange}
                                placeholder="Number of employees trained"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group full">
                        <label htmlFor="training-topics">Training Topics Covered</label>
                        <textarea
                            id="training-topics"
                            name="topic_description"
                            value={formData.topic_description}
                            onChange={handleChange}
                            placeholder="Briefly describe topics (e.g., plan review, de-escalation, reporting)"
                            rows="3"
                            required
                        ></textarea>
                    </div>

                    <div className="compliance-note">
                        <p><strong>Recordkeeping reminder:</strong> Review applicable training-record requirements with qualified counsel. CompCleared does not provide legal advice.</p>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Training Record'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TrainingLog;
